
import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4a4m5b6dpbkhrueeip0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: 'ri-phone-line',
      title: 'Phone',
      info: '+91 9327380066',
      description: '24/7 Emergency Service'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email',
      info: 'zaidenterprise0066@gmail.com',
      description: 'Quick Response Guaranteed'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Location',
      info: 'Karishma Complex, Opposite Muslim Hall, Navsari, Gujarat - 396445',
      description: 'Service Area Coverage'
    },
    {
      icon: 'ri-time-line',
      title: 'Hours',
      info: 'Mon-Fri: 8AM-6PM',
      description: 'Emergency: 24/7'
    }
  ];

  return (
    <section id="contact" className="py-12 sm:py-16 lg:py-20 bg-white" ref={ref}>
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div 
          className="text-center mb-10 sm:mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-[#333333] mb-3 sm:mb-4">Contact Us</h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Ready to discuss your power generation needs? Get in touch with our experts for 
            fast quotes, emergency service, or technical consultation.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-xl sm:text-2xl font-bold text-[#333333] mb-6 sm:mb-8">Get In Touch</h3>
            <div className="grid gap-4 sm:gap-6 mb-6 sm:mb-8">
              {contactInfo.map((item, index) => (
                <motion.div 
                  key={index} 
                  className="flex items-start space-x-3 sm:space-x-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-primary text-lg sm:text-xl`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-[#333333] mb-1 text-sm sm:text-base">{item.title}</h4>
                    <p className="text-[#333333] font-medium text-sm sm:text-base break-words">{item.info}</p>
                    <p className="text-xs sm:text-sm text-gray-600">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div 
              className="bg-accent-50 rounded-xl p-4 sm:p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h4 className="text-base sm:text-lg font-bold text-[#333333] mb-3 sm:mb-4">Emergency Service Available</h4>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                Generator breakdown? We provide 24/7 emergency repair services to get your 
                power back online quickly. Call our emergency hotline for immediate assistance.
              </p>
              <a 
                href="tel:+919327380066"
                className="inline-block bg-accent text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base w-full sm:w-auto text-center"
              >
                <i className="ri-phone-line mr-2"></i>
                Emergency: +91 9327380066
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8 }}
          >
            <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-4 sm:p-6 lg:p-8" data-readdy-form id="contact-form">
              <h3 className="text-xl sm:text-2xl font-bold text-[#333333] mb-4 sm:mb-6">Request a Quote</h3>
              
              <div className="grid gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">Service Needed</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select a service</option>
                    <option value="repair">Generator Repair</option>
                    <option value="maintenance">Maintenance Service</option>
                    <option value="sales-new">New Generator Sales</option>
                    <option value="sales-used">Used Generator Sales</option>
                    <option value="purchase">Generator Purchase</option>
                    <option value="consultation">Technical Consultation</option>
                  </select>
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium text-[#333333] mb-2">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your power generation needs, generator specifications, or any specific requirements..."
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-4 p-3 sm:p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm">
                  Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-4 p-3 sm:p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                  Sorry, there was an error sending your message. Please try again or call us directly.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent text-white py-3 px-6 rounded-lg font-semibold hover:bg-accent-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
